import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
} from '@angular/core';
import { AbstractControl, NgControl } from '@angular/forms';
import { Observable, Subject, Subscription, catchError, map, of, switchMap, takeUntil, timer } from 'rxjs';

import { AddressSuggestion, PostalCodeLookupResult } from './address-autocomplete.model';
import { AddressAutocompleteService } from './address-autocomplete.service';
import { resolveAddressAutocompleteCountry } from './address-autocomplete-policy.util';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;
const ATTRIBUTION_TEXT = 'Powered by Google';
const AUTOCOMPLETE_ATTR = 'autocomplete';
const AUTOCOMPLETE_OFF = 'off';
// Máximo de caracteres de los campos de dirección (address, state, city)
const TEXT_MAX_LENGTH = 255;
const STREET_MIN_LENGTH = 3;
const WORD_SEPARATOR = ' ';
const WHITESPACE_RUN = /\s+/g;
const ACCESSIBILITY_ATTRS: Record<string, string> = {
  role: 'combobox',
  'aria-autocomplete': 'list',
  'aria-haspopup': 'listbox',
  'aria-expanded': 'false',
};

/**
 * Autocompleta la dirección con sugerencias. Al elegir escribe la calle (texto principal) y resuelve
 * código postal (si es válido), país, estado y ciudad (siguen siendo editables) en los controles
 * `postalControlName`, `countryControlName`, `stateControlName` y `cityControlName`.
 * Solo actúa si `enabled` es true y el país del formulario (control `countryControlName`)
 * pertenece a la política centralizada (`address-autocomplete-policy.util.ts`: todos los países de la lista).
 * Panel basado en el patrón de CountrySelectDirective.
 */
@Directive({
  selector: 'input[appAddressAutocomplete]',
})
export class AddressAutocompleteDirective implements OnChanges, AfterViewInit, OnDestroy {
  private static idCounter = 0;
  private readonly panelId = `address-autocomplete-panel-${AddressAutocompleteDirective.idCounter++}`;

  @Input('appAddressAutocomplete') enabled: boolean | '' | null = false;
  @Input() countryControlName = 'country';
  @Input() stateControlName = 'state';
  @Input() cityControlName = 'city';
  @Input() postalControlName = 'zipCode';

  private readonly query$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private lookupSub?: Subscription;
  private selectionId = 0;
  // Valores escritos por la última selección exitosa, para limpiarlos si la siguiente no resuelve
  private autofilled: { postalCode?: string; province?: string; city?: string } = {};
  private originalAutocomplete: string | null = null;
  private attrsApplied = false;

  private suggestions: AddressSuggestion[] = [];
  private highlightIndex = -1;
  private panelEl: HTMLElement | null = null;
  private unlistenDocClick?: () => void;
  private unlistenScroll?: () => void;
  private unlistenResize?: () => void;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
    private service: AddressAutocompleteService,
    @Self() @Optional() private ngControl: NgControl,
  ) {}

  private get postalCodeControl(): AbstractControl | null {
    return this.ngControl?.control?.parent?.get(this.postalControlName) ?? null;
  }

  private get countryControl(): AbstractControl | null {
    return this.ngControl?.control?.parent?.get(this.countryControlName) ?? null;
  }

  private get provinceControl(): AbstractControl | null {
    return this.ngControl?.control?.parent?.get(this.stateControlName) ?? null;
  }

  private get cityControl(): AbstractControl | null {
    return this.ngControl?.control?.parent?.get(this.cityControlName) ?? null;
  }

  // La política de países vive solo en address-autocomplete-policy.util
  private get resolvedCountry(): string | null {
    return resolveAddressAutocompleteCountry(this.countryControl?.value);
  }

  private get isActive(): boolean {
    return this.enabled === true && this.resolvedCountry !== null;
  }

  ngOnChanges(): void {
    this.syncActiveState();
  }

  private syncActiveState(): void {
    if (this.isActive) {
      this.applyHostAttributes();
      return;
    }
    if (this.attrsApplied) {
      this.selectionId++;
      this.lookupSub?.unsubscribe();
    }
    this.restoreHostAttributes();
  }

  private applyHostAttributes(): void {
    if (this.attrsApplied) return;
    const input = this.el.nativeElement;
    this.originalAutocomplete = input.getAttribute(AUTOCOMPLETE_ATTR);
    this.renderer.setAttribute(input, AUTOCOMPLETE_ATTR, AUTOCOMPLETE_OFF);
    Object.entries(ACCESSIBILITY_ATTRS).forEach(([name, value]) => this.renderer.setAttribute(input, name, value));
    this.attrsApplied = true;
  }

  private restoreHostAttributes(): void {
    if (!this.attrsApplied) return;
    const input = this.el.nativeElement;
    this.closePanel();
    this.query$.next('');
    if (this.originalAutocomplete === null) {
      this.renderer.removeAttribute(input, AUTOCOMPLETE_ATTR);
    } else {
      this.renderer.setAttribute(input, AUTOCOMPLETE_ATTR, this.originalAutocomplete);
    }
    Object.keys(ACCESSIBILITY_ATTRS).forEach((name) => this.renderer.removeAttribute(input, name));
    this.attrsApplied = false;
  }

  ngAfterViewInit(): void {
    this.syncActiveState();
    this.countryControl?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncActiveState());
    this.query$
      .pipe(
        map((text) => text.trim()),
        // Texto corto/vacío (cancelación) se procesa al instante; solo la búsqueda espera el debounce
        switchMap((text) =>
          text.length < MIN_QUERY_LENGTH
            ? of({ term: text, suggestions: [] as AddressSuggestion[] })
            : timer(DEBOUNCE_MS).pipe(
                switchMap(() => this.suggestFor(text)),
                map((suggestions) => ({ term: text, suggestions })),
              ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(({ term, suggestions }) => this.showSuggestions(term, suggestions));
  }

  private suggestFor(text: string): Observable<AddressSuggestion[]> {
    const country = this.resolvedCountry;
    if (!country) return of<AddressSuggestion[]>([]);
    return this.service.suggest(text, country).pipe(catchError(() => of<AddressSuggestion[]>([])));
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (!this.isActive) return;
    const value = (event.target as HTMLInputElement | null)?.value ?? '';
    this.selectionId++;
    this.lookupSub?.unsubscribe();
    if (value.trim().length < MIN_QUERY_LENGTH) this.closePanel();
    this.query$.next(value);
  }

  @HostListener('blur')
  onBlur(): void {
    this.leaveField();
  }

  // Cierra el panel y cancela debounce/petición en vuelo para que no reaparezca sin foco
  private leaveField(): void {
    this.closePanel();
    this.query$.next('');
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab' || event.key === 'Escape') {
      if (event.key === 'Escape' && this.panelEl) event.preventDefault();
      this.leaveField();
      return;
    }
    if (!this.panelEl) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
      case 'Enter':
        if (this.suggestions[this.highlightIndex]) {
          event.preventDefault();
          this.select(this.suggestions[this.highlightIndex]);
        }
        break;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.lookupSub?.unsubscribe();
    this.closePanel();
    this.restoreHostAttributes();
  }

  private select(suggestion: AddressSuggestion): void {
    const country = this.resolvedCountry;
    if (!country) return;
    this.closePanel();
    this.query$.next(''); // cancela búsquedas pendientes sin re-disparar por el setValue
    const street = this.streetFrom(suggestion);
    if (street) this.ngControl?.control?.setValue(street);
    const originAddress = this.ngControl?.control?.value;
    const selectionId = ++this.selectionId;
    this.lookupSub?.unsubscribe();
    this.lookupSub = this.service
      .lookupPostalCode({ placeId: suggestion.placeId, description: suggestion.description }, country)
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        // Descarta si la dirección de origen cambió (otra selección, edición o setValue programático)
        if (this.selectionId !== selectionId || this.ngControl?.control?.value !== originAddress) return;
        this.applyLookup(result);
      });
  }

  private applyLookup(result: PostalCodeLookupResult | null): void {
    if (!result) {
      this.clearAutofilled();
      return;
    }
    if (!result.postalCode) this.clearAutofilledPostalCode();
    const province = result.province ? this.toFieldText(result.province) : '';
    if (!province) this.clearAutofilledProvince();
    const city = result.city ? this.toFieldText(result.city) : '';
    if (!city) this.clearAutofilledCity();
    this.autofilled = {};
    if (result.postalCode) {
      this.autofilled.postalCode = result.postalCode;
      this.postalCodeControl?.setValue(result.postalCode);
    }
    // Solo si cambia: un setValue igual dispararía valueChanges y limpiaría la provincia del formulario
    const countryCode = result.countryCode.toUpperCase();
    if (this.countryControl && this.normalizedCountryValue !== countryCode) this.countryControl.setValue(countryCode);
    if (province) {
      this.autofilled.province = province;
      this.provinceControl?.setValue(province);
    }
    if (city) {
      this.autofilled.city = city;
      this.cityControl?.setValue(city);
    }
  }

  // Limpia solo lo autocompletado por la selección anterior que el usuario no editó después
  private clearAutofilled(): void {
    this.clearAutofilledPostalCode();
    this.clearAutofilledProvince();
    this.clearAutofilledCity();
    this.autofilled = {};
  }

  private clearAutofilledCity(): void {
    const { city } = this.autofilled;
    if (city && this.cityControl?.value === city) this.cityControl.setValue('');
  }

  private clearAutofilledProvince(): void {
    const { province } = this.autofilled;
    if (province && this.provinceControl?.value === province) this.provinceControl.setValue('');
  }

  private clearAutofilledPostalCode(): void {
    const { postalCode } = this.autofilled;
    if (postalCode && this.postalCodeControl?.value === postalCode) this.postalCodeControl.setValue('');
  }

  private get normalizedCountryValue(): string {
    return String(this.countryControl?.value ?? '').trim().toUpperCase();
  }

  // mainText si cumple el mínimo; si no, description saneada si lo cumple.
  // Si ninguno lo cumple se conserva mainText no vacío (o description) y el validador mostrará el error.
  private streetFrom(suggestion: AddressSuggestion): string {
    const main = this.toFieldText(suggestion.mainText);
    if (main.length >= STREET_MIN_LENGTH) return main;
    const description = this.toFieldText(suggestion.description);
    return description.length >= STREET_MIN_LENGTH ? description : main || description;
  }

  // Saneamiento mínimo: conserva comas, puntos, #, guiones y tildes; solo colapsa espacios, recorta
  // y limita a TEXT_MAX_LENGTH.
  private toFieldText(text: string | null | undefined): string {
    return (text ?? '').replace(WHITESPACE_RUN, WORD_SEPARATOR).trim().slice(0, TEXT_MAX_LENGTH).trim();
  }

  private showSuggestions(term: string, suggestions: AddressSuggestion[]): void {
    this.suggestions = suggestions;
    // Una respuesta tardía no debe abrir el panel si el campo ya no está activo, perdió el foco
    // o el término de la respuesta ya no coincide con el valor actual
    if (
      suggestions.length === 0 ||
      !this.isActive ||
      document.activeElement !== this.el.nativeElement ||
      this.el.nativeElement.value.trim() !== term
    ) {
      this.closePanel();
      return;
    }
    if (!this.panelEl) this.openPanel();
    this.renderOptions();
  }

  private openPanel(): void {
    const panel = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setAttribute(panel, 'role', 'listbox');
    this.renderer.setAttribute(panel, 'id', this.panelId);
    this.renderer.setAttribute(this.el.nativeElement, 'aria-controls', this.panelId);
    this.renderer.setAttribute(this.el.nativeElement, 'aria-expanded', 'true');
    Object.assign(panel.style, {
      position: 'fixed',
      zIndex: '10000',
      background: '#ffffff',
      border: '1px solid #d0d5dd',
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
      maxHeight: '260px',
      overflowY: 'auto',
      padding: '4px',
      boxSizing: 'border-box',
      font: 'inherit',
      fontSize: '14px',
    });
    this.renderer.appendChild(document.body, panel);
    this.panelEl = panel;
    this.reposition();
    this.unlistenDocClick = this.renderer.listen('document', 'mousedown', (event: MouseEvent) => {
      const target = event.target as Node;
      if (this.el.nativeElement.contains(target) || this.panelEl?.contains(target)) return;
      this.closePanel();
    });
    this.unlistenScroll = this.renderer.listen('window', 'scroll', () => this.closePanel());
    this.unlistenResize = this.renderer.listen('window', 'resize', () => this.reposition());
  }

  private closePanel(): void {
    if (!this.panelEl) return;
    this.renderer.removeChild(document.body, this.panelEl);
    this.panelEl = null;
    this.highlightIndex = -1;
    this.renderer.setAttribute(this.el.nativeElement, 'aria-expanded', 'false');
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-controls');
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-activedescendant');
    this.unlistenDocClick?.();
    this.unlistenScroll?.();
    this.unlistenResize?.();
    this.unlistenDocClick = this.unlistenScroll = this.unlistenResize = undefined;
  }

  private reposition(): void {
    if (!this.panelEl) return;
    const rect = this.el.nativeElement.getBoundingClientRect();
    Object.assign(this.panelEl.style, {
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });
  }

  private renderOptions(): void {
    if (!this.panelEl) return;
    this.panelEl.innerHTML = '';

    this.suggestions.forEach((suggestion, index) => {
      const option = this.renderer.createElement('div') as HTMLElement;
      this.renderer.setAttribute(option, 'role', 'option');
      this.renderer.setAttribute(option, 'id', `${this.panelId}-option-${index}`);
      this.renderer.setAttribute(option, 'aria-selected', 'false');
      Object.assign(option.style, {
        padding: '9px 12px',
        borderRadius: '6px',
        cursor: 'pointer',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      });
      const main = this.renderer.createElement('strong');
      this.renderer.appendChild(main, this.renderer.createText(suggestion.mainText));
      const secondary = this.renderer.createElement('span') as HTMLElement;
      Object.assign(secondary.style, { color: '#667085', marginLeft: '6px' });
      this.renderer.appendChild(secondary, this.renderer.createText(suggestion.secondaryText));
      this.renderer.appendChild(option, main);
      this.renderer.appendChild(option, secondary);

      // mousedown + preventDefault: el input no pierde foco (no dispara blur)
      this.renderer.listen(option, 'mousedown', (event: Event) => {
        event.preventDefault();
        this.select(suggestion);
      });
      this.renderer.listen(option, 'mouseenter', () => this.setHighlight(index));
      this.renderer.appendChild(this.panelEl!, option);
    });

    const footer = this.renderer.createElement('div') as HTMLElement;
    Object.assign(footer.style, {
      padding: '6px 12px',
      fontSize: '11px',
      color: '#98a2b3',
      textAlign: 'right',
    });
    this.renderer.appendChild(footer, this.renderer.createText(ATTRIBUTION_TEXT));
    this.renderer.appendChild(this.panelEl, footer);

    this.setHighlight(0);
    this.reposition();
  }

  private setHighlight(index: number): void {
    if (!this.panelEl) return;
    this.highlightIndex = index;
    let activeId: string | null = null;
    this.suggestions.forEach((_, i) => {
      const child = this.panelEl!.children[i] as HTMLElement;
      const active = i === index;
      child.style.background = active ? '#f2f4f7' : 'transparent';
      child.setAttribute('aria-selected', active ? 'true' : 'false');
      if (active) activeId = child.id;
    });
    if (activeId) this.renderer.setAttribute(this.el.nativeElement, 'aria-activedescendant', activeId);
  }

  private moveHighlight(delta: number): void {
    const next = (this.highlightIndex + delta + this.suggestions.length) % this.suggestions.length;
    this.setHighlight(next);
    this.panelEl?.children[next]?.scrollIntoView({ block: 'nearest' });
  }
}
